import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpClient, IGNORED_ERROR_STATUSES } from '@peca/commons';
import { Pageable } from '@peca/backoffice/models/pageable.model';
import { AutocompleteItem } from '@peca/design-system';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'apps/backoffice/src/environments/environment';
import {
  CreateProductGroupDefaultDto,
  ProductGroupDefaultDto,
  ProductGroupDefaultQuery,
  UpdateProductGroupDefaultDto,
} from '../models/product-group-default.model';
import { ProductGroupSuggestionDto } from '../models/anymarket-publication.model';

@Injectable({ providedIn: 'root' })
export class ProductGroupDefaultService extends BaseHttpClient {
  private readonly api = `${environment.gateway.url}/backoffice/v2/product-group-defaults`;
  private readonly productGroupsApi = `${environment.gateway.url}/backoffice/v2/product-groups`;

  list(queryOrPage: ProductGroupDefaultQuery | number = {}, size?: number): Observable<Pageable<ProductGroupDefaultDto>> {
    let query: ProductGroupDefaultQuery;
    if (typeof queryOrPage === 'number') {
      query = { page: queryOrPage, size: size ?? 12, sort: 'productGroupName,asc' };
    } else {
      query = {
        sort: 'productGroupName,asc',
        page: 1,
        size: 12,
        ...queryOrPage,
      };
    }
    return this.http.get<Pageable<ProductGroupDefaultDto>>(this.api, {
      params: this.prepareQuery(query),
    });
  }

  getById(id: string, ignoreNotFound = false): Observable<ProductGroupDefaultDto> {
    const context = ignoreNotFound ? new HttpContext().set(IGNORED_ERROR_STATUSES, [404]) : undefined;
    return this.http.get<ProductGroupDefaultDto>(`${this.api}/${id}`, { context });
  }

  create(payload: CreateProductGroupDefaultDto): Observable<ProductGroupDefaultDto> {
    return this.http.post<ProductGroupDefaultDto>(this.api, payload);
  }

  update(id: string, payload: UpdateProductGroupDefaultDto): Observable<ProductGroupDefaultDto> {
    return this.http.put<ProductGroupDefaultDto>(`${this.api}/${id}`, payload);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.api}/${id}`);
  }

  suggestProductGroups(search: string): Observable<AutocompleteItem[]> {
    return this.http
      .get<ProductGroupSuggestionDto[]>(`${this.productGroupsApi}/suggestions`, {
        params: this.prepareQuery({ search }),
      })
      .pipe(map((items) => items.map(({ id, name }) => ({ id: String(id), label: name }))));
  }
}
